import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Plus, Pencil, Trash2, KeyRound, MoreVertical, Building2, User as UserIcon } from 'lucide-react';

const ALL_PERMS = ['dashboard', 'tables', 'menu', 'orders', 'kds', 'payments', 'inventory', 'reports', 'users', 'settings'];
const ROLE_KEYS = ['admin', 'manager', 'cashier', 'waiter'];

export default function GestoreUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { username: '', name: '', role: 'waiter', active: true, permissions: ['tables', 'orders'], restaurant_id: '' };
  const [f, setF] = useState(blank);

  const load = async () => {
    const [u, r] = await Promise.all([
      adminApi.get('/admin/users', { params: filter === 'all' ? {} : { restaurant_id: filter } }),
      adminApi.get('/admin/restaurants'),
    ]);
    setUsers(u.data); setRestaurants(r.data);
  };
  useEffect(() => { load(); }, [filter]);

  const restName = (id) => restaurants.find((r) => r.id === id)?.name || '—';

  const openCreate = () => { setEditing(null); setF({ ...blank, restaurant_id: restaurants[0]?.id || '' }); setDialog(true); };
  const openEdit = (u) => { setEditing(u); setF({ ...u }); setDialog(true); };

  const save = async () => {
    if (!f.username.trim() || !f.name.trim()) { toast.error(t('gestore.uUsernameNameReq')); return; }
    const { password, pin, demo_password, demo_pin, credentials_changed, ...clean } = f;
    const payload = { ...clean, restaurant_id: f.restaurant_id || null };
    try {
      if (editing) await adminApi.put(`/admin/users/${editing.id}`, payload);
      else await adminApi.post('/admin/users', payload);
      toast.success(t('gestore.uUserSaved')); setDialog(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('gestore.uError')); }
  };
  const del = async (u) => { if (window.confirm(t('gestore.uDeleteConfirm', { name: u.name }))) { try { await adminApi.delete(`/admin/users/${u.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || t('gestore.uError')); } } };
  const toggle = async (u) => { await adminApi.post(`/admin/users/${u.id}/toggle-active`); load(); };
  const togglePerm = (p) => setF((s) => ({ ...s, permissions: s.permissions.includes(p) ? s.permissions.filter((x) => x !== p) : [...s.permissions, p] }));

  const CredCell = ({ user }) => (
    user.credentials_changed
      ? <span className="text-xs text-muted-foreground italic">{t('gestore.uCredChanged', { defaultValue: 'Modificata dall\'utente' })}</span>
      : <span className="font-mono text-sm">{(user.demo_password || '—')} <span className="text-muted-foreground">/</span> {(user.demo_pin || '—')}</span>
  );

  const StatusBadge = ({ user }) => (
    <button
      onClick={() => toggle(user)}
      data-testid={`gestore-user-status-${user.id}`}
      className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
    >
      <Badge variant={user.active ? 'default' : 'secondary'} className="cursor-pointer">
        {user.active ? t('gestore.uActive') : t('gestore.uInactive')}
      </Badge>
    </button>
  );

  const RowActions = ({ user }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`gestore-user-actions-${user.id}`} aria-label={t('gestore.actions')}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => openEdit(user)} data-testid={`gestore-user-edit-${user.id}`}>
          <Pencil className="h-4 w-4 mr-2" /> {t('gestore.rEdit')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => del(user)} className="text-destructive focus:text-destructive" data-testid={`gestore-user-delete-${user.id}`}>
          <Trash2 className="h-4 w-4 mr-2" /> {t('gestore.rDelete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
      <GestorePageHeader title={t('gestore.uTitle')} subtitle={t('gestore.uSubtitle')}>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="gestore-users-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t('gestore.uAllRestaurants')}</SelectItem>{restaurants.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button className="rounded-xl" data-testid="gestore-add-user-button" onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> {t('gestore.uUserBtn')}</Button>
      </GestorePageHeader>

      {/* Desktop table */}
      <Card className="p-0 hidden md:block overflow-hidden">
        <Table data-testid="gestore-users-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('gestore.uColName')}</TableHead>
              <TableHead>{t('gestore.uColUsername')}</TableHead>
              <TableHead>{t('gestore.uColRole')}</TableHead>
              <TableHead>{t('gestore.uColCreds', { defaultValue: 'Credenziali predefinite' })}</TableHead>
              <TableHead>{t('gestore.uColRestaurant')}</TableHead>
              <TableHead>{t('gestore.uColStatus')}</TableHead>
              <TableHead className="text-right">{t('gestore.uColActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`gestore-users-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.username}</TableCell>
                <TableCell>{t(`roles.${u.role}`)}</TableCell>
                <TableCell><CredCell user={u} /></TableCell>
                <TableCell className="text-sm">{restName(u.restaurant_id)}</TableCell>
                <TableCell><StatusBadge user={u} /></TableCell>
                <TableCell className="text-right"><RowActions user={u} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden flex flex-col gap-3" data-testid="gestore-users-card-list">
        {users.map((u) => (
          <Card key={u.id} className="p-4" data-testid={`gestore-users-card-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold leading-tight truncate">{u.name}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <UserIcon className="h-3 w-3" /> {u.username} · {t(`roles.${u.role}`)}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <StatusBadge user={u} />
                <RowActions user={u} />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="flex flex-col gap-0.5">
                <span className="uppercase tracking-wide text-muted-foreground">{t('gestore.uColCreds', { defaultValue: 'Credenziali predefinite' })}</span>
                <CredCell user={u} />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="uppercase tracking-wide text-muted-foreground">{t('gestore.uColRestaurant')}</span>
                <span className="text-foreground font-medium flex items-center gap-1 truncate"><Building2 className="h-3 w-3 shrink-0" /> {restName(u.restaurant_id)}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{editing ? t('gestore.uEditUser') : t('gestore.uNewUser')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>{t('gestore.uName')}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="gestore-user-name-input" /></div>
              <div><Label>{t('gestore.uUsername')}</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="mt-1.5" disabled={!!editing} data-testid="gestore-user-username-input" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>{t('gestore.uRole')}</Label><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="mt-1.5" data-testid="gestore-user-role-select"><SelectValue /></SelectTrigger><SelectContent>{ROLE_KEYS.map((k) => <SelectItem key={k} value={k}>{t(`roles.${k}`)}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>{t('gestore.uRestaurant')}</Label><Select value={f.restaurant_id || ''} onValueChange={(v) => setF({ ...f, restaurant_id: v })}><SelectTrigger className="mt-1.5" data-testid="gestore-user-restaurant-select"><SelectValue placeholder={t('gestore.uSelect')} /></SelectTrigger><SelectContent>{restaurants.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            {!editing && (
              <div className="rounded-lg bg-muted/50 border border-border p-3 text-xs text-muted-foreground" data-testid="gestore-user-cred-note">
                {t('gestore.uDefaultNote', { defaultValue: 'Credenziali predefinite: Password "admin123" · PIN "123456". L\'utente potrà cambiarle dall\'app; dopo la modifica non saranno più visibili.' })}
              </div>
            )}
            {editing && (
              <div className="rounded-lg bg-muted/50 border border-border p-3 text-xs" data-testid="gestore-user-cred-view">
                <div className="uppercase tracking-wide text-muted-foreground mb-1">{t('gestore.uColCreds', { defaultValue: 'Credenziali predefinite' })}</div>
                <CredCell user={editing} />
              </div>
            )}
            <div><Label>{t('gestore.uPerms')}</Label><div className="grid grid-cols-2 gap-2 mt-1.5">{ALL_PERMS.map((k) => <label key={k} className="flex items-center gap-2 text-sm h-9 px-2 rounded-lg border border-border cursor-pointer"><input type="checkbox" checked={f.permissions?.includes(k)} onChange={() => togglePerm(k)} /> {t(`users.perms.${k}`)}</label>)}</div></div>
            <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> {t('gestore.uAccountActive')}</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>{t('gestore.uCancel')}</Button><Button onClick={save} data-testid="gestore-user-save-button">{t('gestore.uSave')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
